import {
  Component,
  OnInit,
  OnDestroy,
  HostListener
} from '@angular/core';

import {
  Router,
  NavigationEnd,
  ActivatedRoute
} from '@angular/router';

import { filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import {
  SidebarService,
  ISidebar
} from './sidebar.service';

import menuItems, {
  IMenuItem
} from '../../constants/menu';

import { GlobalService } from '../../services/global.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  menuItems: IMenuItem[] = menuItems;

  selectedParentMenu = '';
  viewingParentMenu = '';
  currentUrl = '';

  sidebar: ISidebar;
  subscription: Subscription;

  closedCollapseList: string[] = [];
  tempMenuArr = '';

  /*
   * This flag tells the NavigationEnd handler that the current
   * navigation was caused by clicking a submenu destination.
   *
   * In that case, the route should remain selected, but the submenu
   * panel should be closed after navigation.
   */
  private closeSubMenuOnNavigationEnd = false;

  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    private activatedRoute: ActivatedRoute,
    private global: GlobalService
  ) {

    this.subscription = this.sidebarService
      .getSidebar()
      .subscribe(
        res => {
          this.sidebar = res;
        },
        err => {
          console.error(
            `An error occurred: ${err.message}`
          );
        }
      );

    /*
     * Keep currentUrl synchronized with the current Angular route.
     */
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),

        map(() => this.activatedRoute),

        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }

          return route;
        })
      )
      .subscribe(route => {

        const path =
          this.router.url.split('?')[0];

        const parametersLength =
          Object.keys(route.snapshot.params).length;

        const pathParts =
          path
            .split('/')
            .slice(
              0,
              path.split('/').length -
              parametersLength
            );

        this.currentUrl =
          pathParts.join('/');
      });

    /*
     * Handle completed route navigation.
     */
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {

        /*
         * Recalculate which main-menu item owns the route.
         * This keeps Diagnostics highlighted while Analytics is open.
         */
        this.selectMenu();

        /*
         * When a submenu destination was clicked, hide the submenu
         * after Angular finishes navigating.
         */
        if (this.closeSubMenuOnNavigationEnd) {

          this.closeSubMenuOnNavigationEnd = false;

          this.viewingParentMenu = '';

          this.hideSubMenuPanel();

        } else {

          const {
            containerClassnames
          } = this.sidebar;

          this.toggle();

          this.sidebarService
            .setContainerClassnames(
              0,
              containerClassnames,
              this.sidebar.selectedMenuHasSubItems
            );
        }

        window.scrollTo(0, 0);
      });
  }

  ngOnInit(): void {

    setTimeout(() => {

      this.selectMenu();

      const {
        containerClassnames
      } = this.sidebar;

      const nextClasses =
        this.getMenuClassesForResize(
          containerClassnames
        );

      this.sidebarService
        .setContainerClassnames(
          0,
          nextClasses.join(' '),
          this.sidebar.selectedMenuHasSubItems
        );

      this.isCurrentMenuHasSubItem();

    }, 100);

    this.tempMenuArr =
      JSON.stringify(menuItems);

    const roles =
      localStorage.getItem(
        this.global.settings.ROLES
      ) || '';

    const rolesArr =
      roles
        .split(',')
        .map(role => role.trim())
        .filter(role => role !== '');

    if (roles.includes('All')) {

      menuItems.forEach(item => {
        item.bool = true;
      });

    } else {

      for (
        let i = 0;
        i < rolesArr.length;
        i++
      ) {

        const menuIndex =
          menuItems.findIndex(item =>
            item.label
              .toLowerCase()
              .trim() ===
            rolesArr[i]
              .toLowerCase()
              .trim()
          );

        if (menuIndex > -1) {

          if (
            menuItems[menuIndex].subs !==
            undefined
          ) {

            let subItems =
              menuItems[menuIndex].subs;

            for (
              let j = 0;
              j < rolesArr.length;
              j++
            ) {

              const subIndex =
                subItems.findIndex(sub =>
                  sub.label
                    .toLowerCase()
                    .trim() ===
                  rolesArr[j]
                    .toLowerCase()
                    .trim()
                );

              if (subIndex > -1) {
                subItems[subIndex].bool = true;
              }
            }

            subItems =
              subItems.filter(
                sub => sub.bool
              );

            menuItems[menuIndex].subs =
              subItems;

            if (
              menuItems[menuIndex].subs.length >
              0
            ) {
              menuItems[menuIndex].bool = true;
            }

          } else {

            menuItems[menuIndex].bool = true;
          }
        }
      }
    }

    this.menuItems =
      menuItems.filter(
        item => item.bool === true
      );
  }

  ngOnDestroy(): void {

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Determines which main-menu item owns the current route.
   *
   * This is used for active-menu highlighting. It should not itself
   * be used as the open/closed state of the submenu panel.
   */
  selectMenu(): void {

    this.selectedParentMenu =
      this.findParentInPath(
        this.currentUrl
      ) || '';

    this.isCurrentMenuHasSubItem();
  }

  /**
   * Navigates to a submenu destination and closes the submenu panel.
   */
  navigateAndCloseSubMenu(
    event: Event,
    route: string
  ): void {

    event.preventDefault();
    event.stopPropagation();

    this.closeSubMenuOnNavigationEnd = true;
    this.viewingParentMenu = '';

    /*
     * Hide immediately to avoid a visible delay while navigation
     * completes.
     */
    this.hideSubMenuPanel();

    /*
     * Clicking Analytics while already on Analytics does not create
     * another NavigationEnd event, so clear the flag immediately.
     */
    const currentPath =
      this.router.url.split('?')[0];

    if (currentPath === route) {

      this.closeSubMenuOnNavigationEnd = false;

      this.currentUrl = route;
      this.selectMenu();
      this.hideSubMenuPanel();

      return;
    }

    this.router
      .navigateByUrl(route)
      .catch(error => {

        this.closeSubMenuOnNavigationEnd = false;

        console.error(
          'Sidebar navigation failed:',
          error
        );
      });
  }

  /**
   * Closes the submenu without changing the current route.
   */
  closeSubMenu(event?: Event): void {

    event?.stopPropagation();

    this.viewingParentMenu = '';

    this.hideSubMenuPanel();
  }

  /**
   * Applies the Vien global CSS class that hides the submenu panel
   * and removes the width reserved for it.
   */
  private hideSubMenuPanel(): void {

    if (!this.sidebar) {
      return;
    }

    const currentClasses =
      (
        this.sidebar.containerClassnames ||
        ''
      )
        .split(' ')
        .filter(className =>
          className !== ''
        );

    /*
     * Remove temporary submenu classes.
     */
    const nextClasses =
      currentClasses.filter(className =>
        className !== 'sub-show-temporary'
      );

    /*
     * menu-sub-hidden is the Vien class that collapses only the
     * secondary submenu panel while leaving the main icon menu visible.
     */
    if (
      !nextClasses.includes(
        'menu-sub-hidden'
      )
    ) {
      nextClasses.push(
        'menu-sub-hidden'
      );
    }

    this.sidebarService
      .setContainerClassnames(
        0,
        nextClasses.join(' '),
        this.sidebar.selectedMenuHasSubItems
      );
  }

  findParentInPath(
    path: string
  ): string | undefined {

    const foundMenuItem =
      this.menuItems.find(
        item => item.to === path
      );

    if (!foundMenuItem) {

      if (
        path &&
        path.split('/').length > 1
      ) {

        const pathParts =
          path.split('/');

        return this.findParentInPath(
          pathParts
            .slice(
              0,
              pathParts.length - 1
            )
            .join('/')
        );
      }

      return undefined;
    }

    return path;
  }

  isCurrentMenuHasSubItem(): boolean {

    if (!this.sidebar) {
      return false;
    }

    const {
      containerClassnames
    } = this.sidebar;

    const menuItem =
      this.menuItems.find(
        item =>
          item.to ===
          this.selectedParentMenu
      );

    const currentMenuHasSubItem =
      !!(
        menuItem &&
        menuItem.subs &&
        menuItem.subs.length > 0
      );

    if (
      currentMenuHasSubItem !==
      this.sidebar.selectedMenuHasSubItems
    ) {

      this.sidebarService
        .setContainerClassnames(
          0,
          containerClassnames,
          currentMenuHasSubItem
        );
    }

    return currentMenuHasSubItem;
  }

  changeSelectedParentHasNoSubmenu(
    parentMenu: string
  ): void {

    const {
      containerClassnames
    } = this.sidebar;

    this.selectedParentMenu =
      parentMenu;

    this.viewingParentMenu =
      parentMenu;

    this.sidebarService
      .changeSelectedMenuHasSubItems(
        false
      );

    this.sidebarService
      .setContainerClassnames(
        0,
        containerClassnames,
        false
      );
  }

  openSubMenu(
    event: {
      preventDefault?: () => void;
      stopPropagation: () => void;
    },
    menuItem: IMenuItem
  ): void {

    if (event) {

      if (event.preventDefault) {
        event.preventDefault();
      }

      event.stopPropagation();
    }

    const {
      containerClassnames,
      menuClickCount
    } = this.sidebar;

    const selectedParent =
      menuItem.to;

    const hasSubMenu =
      !!(
        menuItem.subs &&
        menuItem.subs.length > 0
      );

    this.sidebarService
      .changeSelectedMenuHasSubItems(
        hasSubMenu
      );

    if (!hasSubMenu) {

      this.viewingParentMenu =
        selectedParent;

      this.selectedParentMenu =
        selectedParent;

      this.toggle();

      return;
    }

    const currentClasses =
      containerClassnames
        ? containerClassnames
            .split(' ')
            .filter(className =>
              className !== ''
            )
        : [];

    /*
     * If the submenu was closed by clicking Analytics, clicking
     * Diagnostics should open it again.
     */
    if (
      currentClasses.includes(
        'menu-sub-hidden'
      )
    ) {

      const nextClasses =
        currentClasses.filter(
          className =>
            className !==
            'menu-sub-hidden'
        );

      this.sidebarService
        .setContainerClassnames(
          0,
          nextClasses.join(' '),
          hasSubMenu
        );

    } else if (
      !currentClasses.includes(
        'menu-mobile'
      )
    ) {

      if (
        currentClasses.includes(
          'menu-sub-hidden'
        ) &&
        (
          menuClickCount === 2 ||
          menuClickCount === 0
        )
      ) {

        this.sidebarService
          .setContainerClassnames(
            3,
            containerClassnames,
            hasSubMenu
          );

      } else if (
        currentClasses.includes(
          'menu-hidden'
        ) &&
        (
          menuClickCount === 1 ||
          menuClickCount === 3
        )
      ) {

        this.sidebarService
          .setContainerClassnames(
            2,
            containerClassnames,
            hasSubMenu
          );

      } else if (
        currentClasses.includes(
          'menu-default'
        ) &&
        !currentClasses.includes(
          'menu-sub-hidden'
        ) &&
        (
          menuClickCount === 1 ||
          menuClickCount === 3
        )
      ) {

        this.sidebarService
          .setContainerClassnames(
            0,
            containerClassnames,
            hasSubMenu
          );
      }

    } else {

      this.sidebarService
        .addContainerClassname(
          'sub-show-temporary',
          containerClassnames
        );
    }

    this.viewingParentMenu =
      selectedParent;
  }

  toggle(): void {

    if (!this.sidebar) {
      return;
    }

    const {
      containerClassnames,
      menuClickCount
    } = this.sidebar;

    const currentClasses =
      (
        containerClassnames ||
        ''
      )
        .split(' ')
        .filter(className =>
          className !== ''
        );

    if (
      currentClasses.includes(
        'menu-sub-hidden'
      ) &&
      menuClickCount === 3
    ) {

      this.sidebarService
        .setContainerClassnames(
          2,
          containerClassnames,
          this.sidebar.selectedMenuHasSubItems
        );

    } else if (
      currentClasses.includes(
        'menu-hidden'
      ) ||
      currentClasses.includes(
        'menu-mobile'
      )
    ) {

      if (
        !(
          menuClickCount === 1 &&
          !this.sidebar.selectedMenuHasSubItems
        )
      ) {

        this.sidebarService
          .setContainerClassnames(
            0,
            containerClassnames,
            this.sidebar.selectedMenuHasSubItems
          );
      }
    }
  }

  toggleCollapse(id: string): void {

    if (
      this.closedCollapseList.includes(id)
    ) {

      this.closedCollapseList =
        this.closedCollapseList.filter(
          item => item !== id
        );

    } else {

      this.closedCollapseList.push(id);
    }
  }

  getMenuClassesForResize(
    classes: string
  ): string[] {

    let nextClasses =
      (
        classes ||
        ''
      )
        .split(' ')
        .filter(className =>
          className !== ''
        );

    const windowWidth =
      window.innerWidth;

    if (
      windowWidth <
      this.sidebarService.menuHiddenBreakpoint
    ) {

      if (
        !nextClasses.includes(
          'menu-mobile'
        )
      ) {
        nextClasses.push(
          'menu-mobile'
        );
      }

    } else if (
      windowWidth <
      this.sidebarService.subHiddenBreakpoint
    ) {

      nextClasses =
        nextClasses.filter(
          className =>
            className !== 'menu-mobile'
        );

      if (
        nextClasses.includes(
          'menu-default'
        ) &&
        !nextClasses.includes(
          'menu-sub-hidden'
        )
      ) {

        nextClasses.push(
          'menu-sub-hidden'
        );
      }

    } else {

      nextClasses =
        nextClasses.filter(
          className =>
            className !== 'menu-mobile'
        );
    }

    return nextClasses;
  }

  @HostListener(
    'document:click',
    ['$event']
  )
  handleDocumentClick(
    event: MouseEvent
  ): void {

    this.viewingParentMenu = '';

    /*
     * Keep the active route highlighted.
     */
    this.selectMenu();

    /*
     * Clicking outside the sidebar should close the submenu panel.
     */
    this.hideSubMenuPanel();
  }

  @HostListener(
    'window:resize',
    ['$event']
  )
  handleWindowResize(
    event: Event
  ): void {

    if (
      event &&
      event.isTrusted === false
    ) {
      return;
    }

    if (!this.sidebar) {
      return;
    }

    const {
      containerClassnames
    } = this.sidebar;

    const nextClasses =
      this.getMenuClassesForResize(
        containerClassnames
      );

    this.sidebarService
      .setContainerClassnames(
        0,
        nextClasses.join(' '),
        this.sidebar.selectedMenuHasSubItems
      );

    this.isCurrentMenuHasSubItem();
  }

  menuClicked(
    event: MouseEvent
  ): void {

    event.stopPropagation();
  }
}